import { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import { Network } from '@capacitor/network'
import { Capacitor } from '@capacitor/core'

interface NetworkContextType {
  isOnline: boolean
  connectionType: string
}

const NetworkContext = createContext<NetworkContextType>({
  isOnline: true,
  connectionType: 'unknown'
})

export const useNetwork = () => useContext(NetworkContext)

interface NetworkProviderProps {
  children: ReactNode
}

export const NetworkProvider = ({ children }: NetworkProviderProps) => {
  const [isOnline, setIsOnline] = useState(true)
  const [connectionType, setConnectionType] = useState('unknown')

  useEffect(() => {
    // 初期状態を取得
    const checkInitialStatus = async () => {
      if (Capacitor.isNativePlatform()) {
        // ネイティブアプリの場合
        const status = await Network.getStatus()
        setIsOnline(status.connected)
        setConnectionType(status.connectionType)
      } else {
        // Webの場合
        setIsOnline(navigator.onLine)
        setConnectionType(navigator.onLine ? 'wifi' : 'none')
      }
    }

    checkInitialStatus()

    if (Capacitor.isNativePlatform()) {
      // ネイティブアプリ: Capacitor Network APIを使用
      let listenerHandle: any = null

      const setupListener = async () => {
        listenerHandle = await Network.addListener('networkStatusChange', status => {
          console.log('Network status changed:', status)
          setIsOnline(status.connected)
          setConnectionType(status.connectionType)
        })
      }

      setupListener()

      return () => {
        if (listenerHandle) {
          listenerHandle.remove()
        }
      }
    } else {
      // Web: window.onlineイベントを使用
      const handleOnline = () => {
        console.log('Browser is online')
        setIsOnline(true)
        setConnectionType('wifi')
      }

      const handleOffline = () => {
        console.log('Browser is offline')
        setIsOnline(false)
        setConnectionType('none')
      }

      window.addEventListener('online', handleOnline)
      window.addEventListener('offline', handleOffline)

      return () => {
        window.removeEventListener('online', handleOnline)
        window.removeEventListener('offline', handleOffline)
      }
    }
  }, [])

  return (
    <NetworkContext.Provider value={{ isOnline, connectionType }}>
      {children}
    </NetworkContext.Provider>
  )
}
